import { CoreApplicationContext } from '@eventbrite/context-gen';
import { parseLocationResponse } from '@eventbrite/redux-destination';
import { FacebookOptions } from '../../../types/App';
import {
    BucketsContent,
    Events,
    FeatureFlags,
    GASettings,
} from '../../../types/index';
import {
    getSeasonalContent,
    retrieveHomeFeedState,
    transformBucketsContent,
} from '../utils';
import { getHeaderContent } from './header';

interface minimalInitialStateProps {
    experiments?: any;
    featureFlags?: FeatureFlags;
    gaSettings?: GASettings;
    isOnline?: boolean;
    isBotRequest?: boolean;
    env: CoreApplicationContext['env'];
    facebookOptions?: FacebookOptions;
}

export const minimalInitialState = (props: minimalInitialStateProps) => {
    const {
        experiments,
        featureFlags,
        gaSettings,
        isOnline,
        isBotRequest,
        env,
        facebookOptions,
    } = props;

    const {
        currencyFormat,
        ebDomain: domain,
        isMobile,
        localeInfo: {
            // every locale can use a different twitter account and
            // facebook account, so we need to use them in order to build
            // share messages
            twitter_handle: twitterHandle,
            facebook_locale: facebookLocale,
            facebook_page: facebookPage,
            tld,
            locale,
        },
        serverUrl,
    } = env;
    const seasonalContent = getSeasonalContent({ tld });
    const browseState = retrieveHomeFeedState({ seasonalContent });

    let _header;
    /**
     * We only get the header content when we have the full context loaded
     * to prevent double rendering and getting two images without reason.
     */
    if (tld) {
        /*
         * Get a default override in case we don't have any user info but we
         * do have an override for the tld for that seasonal.
         */
        _header = seasonalContent[0]?.byDate?.tldOverrides?.[tld];
    }

    return {
        currencyFormat,
        gaSettings,
        app: {
            domain,
            tld,
            locale,
            experiments,
            featureFlags: {
                ...featureFlags,
            },
            isMobile,
            loadingState: {
                isFeLoading: true,
                isSearchLoading: false,
                isCityBrowseLoading: false,
                isProfilesLoading: false,
                isOrganizerArticlesLoading: false,
            },
            isBotRequest,
        },
        env,
        content: {
            entities: {},
            entityContext: [],
            flatBucket: [],
            browseState: browseState,
            seasonal: seasonalContent,
        },
        organizers: {
            organizersUserFollow: [],
        },
        location: {
            slug: '',
            placeId: '',
            latitude: 0,
            longitude: 0,
            currentPlace: '',
            currentPlaceParent: '',
            placeType: '',
            topSuggestions: [],
            isOnline,
        },
        search: {
            slug: '',
            placeId: '',
            latitude: '',
            longitude: '',
            currentPlace: '',
            currentPlaceParent: '',
            placeType: '',
            topSuggestions: [],
        },
        user: {
            isAuthenticated: false,
            publicId: '',
            canCreateEvents: true,
        },
        shareOptions: {
            isMobile,
            twitterHandle,
            serverUrl,
            facebookOptions: {
                ...facebookOptions,
                locale: facebookLocale,
                page: facebookPage,
            },
        },
        header: _header,
    };
};

interface InitialState extends minimalInitialStateProps {
    buckets: BucketsContent;
    locationSlug?: string;
    latitude?: number;
    longitude?: number;
    placeId?: string;
    currentPlace?: string;
    currentPlaceParent?: string;
    placeType?: string;
    locations?: string[];
    flatBucket?: {
        result?: Events;
    };
    user: {
        isAuthenticated?: boolean;
        publicId?: string;
        canCreateEvents?: string;
        firstName?: string;
    };
}

export const getInitialState = (props: InitialState) => {
    const {
        buckets,
        experiments,
        featureFlags,
        gaSettings,
        locationSlug,
        placeId,
        latitude,
        longitude,
        currentPlace,
        currentPlaceParent,
        placeType,
        locations,
        flatBucket,
        isOnline,
        isBotRequest,
        env,
        facebookOptions,
        user: { isAuthenticated, publicId, canCreateEvents, firstName },
    } = props;

    const {
        currencyFormat,
        ebDomain: domain,
        isMobile,
        localeInfo: {
            // every locale can use a different twitter account and
            // facebook account, so we need to use them in order to build
            // share messages
            twitter_handle: twitterHandle,
            facebook_locale: facebookLocale,
            facebook_page: facebookPage,
            tld,
            locale,
        },
        serverUrl,
    } = env;
    const seasonalContent = getSeasonalContent({ placeId, tld });
    const browseState = retrieveHomeFeedState({ seasonalContent });
    const { entities, entityContext } = transformBucketsContent(buckets);
    const organizersUserFollow: string[] = [];

    let _header;

    /**
     * We only get the header content when we have the full context loaded
     * to prevent double rendering and getting two images without reason.
     */
    if (currentPlace || placeId || latitude) {
        _header = getHeaderContent({ seasonalContent, tld, locationSlug });
    } else if (tld) {
        /*
         * Get a default override in case we don't have any user info but we
         * do have an override for the tld for that seasonal.
         */
        _header = seasonalContent[0]?.byDate?.tldOverrides?.[tld];
    }

    return {
        currencyFormat,
        gaSettings,
        app: {
            domain,
            tld,
            locale,
            experiments,
            featureFlags,
            isMobile,
            loadingState: {
                isFeLoading: true,
                isSearchLoading: false,
                isCityBrowseLoading: false,
                isProfilesLoading: false,
                isOrganizerArticlesLoading: false,
            },
            isBotRequest,
        },
        env,
        content: {
            entities,
            entityContext,
            flatBucket,
            browseState,
            seasonal: seasonalContent,
        },
        organizers: {
            organizersUserFollow,
        },
        location: {
            slug: locationSlug,
            placeId,
            latitude,
            longitude,
            currentPlace,
            currentPlaceParent,
            placeType,
            topSuggestions: locations ? parseLocationResponse(locations) : [],
            isOnline,
        },
        search: {
            slug: locationSlug,
            placeId,
            latitude,
            longitude,
            currentPlace,
            currentPlaceParent,
            placeType,
            topSuggestions: locations ? parseLocationResponse(locations) : [],
        },
        user: {
            isAuthenticated,
            publicId,
            canCreateEvents,
            firstName,
        },
        shareOptions: {
            isMobile,
            twitterHandle,
            serverUrl,
            facebookOptions: {
                ...facebookOptions,
                locale: facebookLocale,
                page: facebookPage,
            },
        },
        header: _header,
    };
};
